export const DetailedStatisticsParts = ['clicks', 'leads', 'conversions'];

const ADDITIVE_FIELDS = [
  'clicks',
  'cost',
  'leadP0',
  'totalLead',
  'leadOk',
  'leadKo',
  'leadCrossOk',
  'leadCrossTotal',
  'leadCoregOk',
  'leadCoregAAOk',
  'leadCoregTotal',
  'leadDoi',
  'leadAR',
  'leadReactivated',
];

const TOTAL_FIELDS = [
  'clicks',
  'cost',
  'leadP0',
  'leadOk',
  'leadKo',
  'leadCoregOk',
  'leadCoregTotal',
  'leadCoregAAOk',
  'leadCrossOk',
  'leadCrossTotal',
  'leadDoi',
  'leadAR',
  'leadReactivated',
  'totalLead',
];

const ratio = (value: number, total: number, empty: number | null): number | null => total ? value / total : empty;

export function mergeDetailedStatistics(responses: any[]): {rows: any[], totals: any} {
  const rows = new Map<string, any>();

  for (const response of responses) {
    for (const row of response?.rows ?? []) {
      const key = String(row.key);
      const merged = rows.get(key);

      if (!merged) {
        rows.set(key, {...row, goals: mergeGoals([], row.goals)});
        continue;
      }

      for (const field of ADDITIVE_FIELDS) {
        merged[field] = (merged[field] ?? 0) + (row[field] ?? 0);
      }

      merged.label = merged.label || row.label;
      if ('landingPage' in row) {
        merged.landingPage = merged.landingPage ?? row.landingPage;
      }
      merged.goals = mergeGoals(merged.goals, row.goals);
    }
  }

  const mergedRows = [...rows.values()];

  for (const row of mergedRows) {
    computeRowRatios(row);
  }

  return {rows: mergedRows, totals: computeTotals(mergedRows)};
}

function mergeGoals(target: any, source: any): any {
  if (!source || !Object.keys(source).length) {
    return target;
  }

  const merged = Array.isArray(target) ? {} : target;

  for (const [goalName, goalTypes] of Object.entries<any>(source)) {
    merged[goalName] = {...(merged[goalName] ?? {})};

    for (const [goalTypeName, stats] of Object.entries<any>(goalTypes)) {
      const current = merged[goalName][goalTypeName] ?? {amount: 0, count: 0};
      merged[goalName][goalTypeName] = {
        amount: current.amount + stats.amount,
        count: current.count + stats.count,
      };
    }
  }

  return merged;
}

function computeRowRatios(row: any): void {
  const leadOkAR = row.leadOk + row.leadAR;

  row.cpc = ratio(row.cost, row.clicks, 0);
  row.cpl = ratio(row.cost, row.leadOk, 0);
  row.leadOkPercent = ratio(row.leadOk, row.clicks, 0);
  row.leadP0Percent = ratio(row.leadP0, row.clicks, 0);

  row.leadDoiPercent = ratio(row.leadDoi, row.leadOk, null);
  row.leadARPercent = ratio(row.leadAR, row.totalLead, null);
  row.leadCoregOkPercent = ratio(row.leadCoregOk, leadOkAR, null);
  row.leadCoregAAOkPercent = ratio(row.leadCoregAAOk, leadOkAR, null);
  row.leadCrossOkPercent = ratio(row.leadCrossOk, leadOkAR, null);
}

function computeTotals(rows: any[]): any {
  const totals: any = {cpl: 0, goals: {totals: {amount: 0, count: 0}}};

  for (const field of TOTAL_FIELDS) {
    totals[field] = 0;
  }

  for (const row of rows) {
    for (const field of TOTAL_FIELDS) {
      totals[field] += row[field] ?? 0;
    }

    for (const [goalName, goalTypes] of Object.entries<any>(row.goals ?? {})) {
      for (const [goalTypeName, stats] of Object.entries<any>(goalTypes)) {
        totals.goals[goalName] = totals.goals[goalName] ?? {};
        const current = totals.goals[goalName][goalTypeName] ?? {amount: 0, count: 0};
        totals.goals[goalName][goalTypeName] = {
          amount: current.amount + stats.amount,
          count: current.count + stats.count,
        };
        totals.goals.totals.amount += stats.amount;
        totals.goals.totals.count += stats.count;
      }
    }
  }

  const leadOkAR = totals.leadOk + totals.leadAR;

  totals.cpc = ratio(totals.cost, totals.clicks, 0);
  totals.cpl = ratio(totals.cost, totals.leadOk, 0);
  totals.leadOkPercent = ratio(totals.leadOk, totals.clicks, null);
  totals.leadP0Percent = ratio(totals.leadP0, totals.clicks, null);
  totals.leadARPercent = ratio(totals.leadAR, totals.totalLead, null);
  totals.leadCoregOkPercent = ratio(totals.leadCoregOk, leadOkAR, null);
  totals.leadCoregAAOkPercent = ratio(totals.leadCoregAAOk, leadOkAR, null);
  totals.leadCrossOkPercent = ratio(totals.leadCrossOk, leadOkAR, null);
  totals.leadDoiPercent = ratio(totals.leadDoi, totals.leadOk, null);

  return totals;
}
